import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import {
  addTodoComment,
  addTodoLink,
  completeTodo,
  createTodo,
  deleteTodo,
  duplicateTodo,
  listTodoCalendars,
  listTodos,
  moveTodo,
  patchTodo,
  removeTodoAttachment,
  uploadTodoAttachment,
  type Todo,
  type TodoCalendar,
  type TodoPatch,
} from '@/api'
import { reportError } from '@/errorReport'
import { useAuthStore } from '@/stores/auth'
import { TodoSocket } from '@/todos/realtime'
import { collectTags } from '@/todos/tags'
import { descendantUids } from '@/todos/tree'

// There is no server-side todo cache: lists are loaded live from CalDAV and kept here while the
// Mini App is open. Edits apply optimistically and are reverted if the server rejects them; live
// pushes from the websocket never overwrite a todo that still has an edit in flight.
export const useTodosStore = defineStore('todos', () => {
  const calendars = ref<TodoCalendar[]>([])
  const calendarsLoaded = ref(false)
  const todosByCalendar = ref<Record<number, Todo[]>>({})
  const loading = ref<Record<number, boolean>>({})
  const errors = ref<Record<number, string | null>>({})
  const inFlight = new Map<string, number>()
  // Autocomplete source for tag inputs: whatever the loaded lists already use.
  const knownTags = computed(() => collectTags(todosByCalendar.value))

  const socket = new TodoSocket(
    () => useAuthStore().token,
    (calendarId, todos) => applyServerList(calendarId, todos),
  )

  function key(calendarId: number, uid: string): string {
    return `${calendarId}\u0000${uid}`
  }

  function track<T>(calendarId: number, uids: string[], work: () => Promise<T>): Promise<T> {
    const keys = uids.map((uid) => key(calendarId, uid))
    for (const k of keys) inFlight.set(k, (inFlight.get(k) ?? 0) + 1)
    return work().finally(() => {
      for (const k of keys) {
        const left = (inFlight.get(k) ?? 1) - 1
        if (left <= 0) inFlight.delete(k)
        else inFlight.set(k, left)
      }
    })
  }

  function applyServerList(calendarId: number, todos: Todo[]): void {
    const current = todosByCalendar.value[calendarId] ?? []
    const pendingLocal = current.filter((t) => inFlight.has(key(calendarId, t.uid)))
    const pendingUids = new Set(pendingLocal.map((t) => t.uid))
    todosByCalendar.value[calendarId] = [
      ...todos.filter((t) => !pendingUids.has(t.uid)),
      ...pendingLocal,
    ]
  }

  async function loadCalendars(): Promise<void> {
    calendars.value = await listTodoCalendars()
    calendarsLoaded.value = true
  }

  function calendarById(calendarId: number): TodoCalendar | undefined {
    return calendars.value.find((c) => c.id === calendarId)
  }

  async function loadTodos(calendarId: number): Promise<void> {
    loading.value[calendarId] = true
    errors.value[calendarId] = null
    try {
      const result = await listTodos(calendarId)
      applyServerList(calendarId, result.todos)
      const calendar = calendarById(calendarId)
      if (calendar) calendar.cached_task_count = result.todos.length
    } catch (e) {
      reportError(e, 'todos.load')
      errors.value[calendarId] = e instanceof Error ? e.message : String(e)
    } finally {
      loading.value[calendarId] = false
    }
  }

  function todosOf(calendarId: number): Todo[] {
    return todosByCalendar.value[calendarId] ?? []
  }

  function findTodo(calendarId: number, uid: string): Todo | undefined {
    return todosOf(calendarId).find((t) => t.uid === uid)
  }

  function replaceTodo(calendarId: number, uid: string, todo: Todo): void {
    const list = todosOf(calendarId)
    const index = list.findIndex((t) => t.uid === uid)
    if (index >= 0) list.splice(index, 1, todo)
    else list.push(todo)
    todosByCalendar.value[calendarId] = list
  }

  function removeTodos(calendarId: number, uids: string[]): void {
    const drop = new Set(uids)
    todosByCalendar.value[calendarId] = todosOf(calendarId).filter((t) => !drop.has(t.uid))
  }

  // Applies `local` right away, then replaces it with whatever the server returns -- or restores
  // the previous state and rethrows if the request fails.
  async function optimistic(
    calendarId: number,
    uid: string,
    local: Partial<Todo>,
    request: () => Promise<Todo>,
  ): Promise<Todo> {
    const previous = findTodo(calendarId, uid)
    if (previous) replaceTodo(calendarId, uid, { ...previous, ...local })
    try {
      const saved = await track(calendarId, [uid], request)
      replaceTodo(calendarId, uid, saved)
      return saved
    } catch (e) {
      if (previous) replaceTodo(calendarId, uid, previous)
      throw e
    }
  }

  function update(calendarId: number, uid: string, changes: TodoPatch): Promise<Todo> {
    return optimistic(calendarId, uid, changes as Partial<Todo>, () =>
      patchTodo(calendarId, uid, changes),
    )
  }

  function setDone(calendarId: number, uid: string, done: boolean): Promise<Todo> {
    const local: Partial<Todo> = done
      ? { status: 'COMPLETED', completed_at: new Date().toISOString() }
      : { status: null, completed_at: null }
    return optimistic(calendarId, uid, local, () => completeTodo(calendarId, uid, done))
  }

  async function create(
    calendarId: number,
    fields: TodoPatch & { summary: string },
  ): Promise<Todo> {
    const tempUid = `pending-${crypto.randomUUID()}`
    const placeholder: Todo = {
      uid: tempUid,
      description: null,
      status: null,
      completed_at: null,
      percent_complete: null,
      priority: 0,
      start: null,
      due: null,
      categories: [],
      url: null,
      location: null,
      geo: null,
      privacy: null,
      rrule: null,
      alarms: [],
      comments: [],
      attachments: [],
      assignees: [],
      parent_uid: null,
      sort_order: null,
      flagged: false,
      event_status: null,
      show_as: null,
      created: new Date().toISOString(),
      last_modified: null,
      ...(fields as Partial<Todo>),
      summary: fields.summary,
    }
    replaceTodo(calendarId, tempUid, placeholder)
    try {
      const saved = await track(calendarId, [tempUid], () => createTodo(calendarId, fields))
      removeTodos(calendarId, [tempUid])
      replaceTodo(calendarId, saved.uid, saved)
      return saved
    } catch (e) {
      removeTodos(calendarId, [tempUid])
      throw e
    }
  }

  async function remove(
    calendarId: number,
    uid: string,
    subtasks: 'delete' | 'orphan',
  ): Promise<void> {
    const snapshot = [...todosOf(calendarId)]
    const removed = subtasks === 'delete' ? [uid, ...descendantUids(snapshot, uid)] : [uid]
    removeTodos(calendarId, removed)
    if (subtasks === 'orphan') {
      for (const t of todosOf(calendarId))
        if (t.parent_uid === uid) replaceTodo(calendarId, t.uid, { ...t, parent_uid: null })
    }
    try {
      await track(calendarId, removed, () => deleteTodo(calendarId, uid, subtasks))
    } catch (e) {
      todosByCalendar.value[calendarId] = snapshot
      throw e
    }
  }

  async function duplicate(calendarId: number, uid: string): Promise<Todo> {
    const copy = await duplicateTodo(calendarId, uid)
    replaceTodo(calendarId, copy.uid, copy)
    return copy
  }

  async function move(calendarId: number, uid: string, targetCalendarId: number): Promise<Todo> {
    const removed = [uid, ...descendantUids(todosOf(calendarId), uid)]
    const moved = await track(calendarId, removed, () =>
      moveTodo(calendarId, uid, targetCalendarId),
    )
    removeTodos(calendarId, removed)
    if (todosByCalendar.value[targetCalendarId]) await loadTodos(targetCalendarId)
    return moved
  }

  async function withResult(
    calendarId: number,
    uid: string,
    request: () => Promise<Todo>,
  ): Promise<Todo> {
    const saved = await track(calendarId, [uid], request)
    replaceTodo(calendarId, uid, saved)
    return saved
  }

  const addComment = (calendarId: number, uid: string, text: string) =>
    withResult(calendarId, uid, () => addTodoComment(calendarId, uid, text))
  const addLink = (calendarId: number, uid: string, uri: string, filename?: string) =>
    withResult(calendarId, uid, () => addTodoLink(calendarId, uid, uri, filename))
  const upload = (calendarId: number, uid: string, file: File) =>
    withResult(calendarId, uid, () => uploadTodoAttachment(calendarId, uid, file))
  const removeAttachment = (calendarId: number, uid: string, index: number) =>
    withResult(calendarId, uid, () => removeTodoAttachment(calendarId, uid, index))

  function watch(calendarId: number): () => void {
    socket.subscribe(calendarId)
    return () => socket.unsubscribe(calendarId)
  }

  return {
    calendars,
    calendarsLoaded,
    todosByCalendar,
    knownTags,
    loading,
    errors,
    loadCalendars,
    calendarById,
    loadTodos,
    todosOf,
    findTodo,
    applyServerList,
    update,
    setDone,
    create,
    remove,
    duplicate,
    move,
    addComment,
    addLink,
    upload,
    removeAttachment,
    watch,
  }
})
